/**
 * A quickly hacked-together way for helpdesk to work tickets after Rational is dead and until the new CSM SNOW instance is deployed.
 * This came about because of an issue with the Privacy Reduction Act (PRA). This file probably won't be alive in the codebase for more than a few weeks.
 *
 * For fun and ease of deleting later I decided to put all of the frontend code into a single component.
 *
 * @author Alex Clary
 * @prettier
 *
 * */
import React, { useEffect, useState } from 'react'
import { useParams } from 'react-router'
import LgyLink from 'app/components/util/lgy-link/LgyLink'
import LgyField from 'app/components/util/lgy-field/LgyField'
import { getCookieValue } from 'app/actions/genericActionCreator'
import DatePicker from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'

const HelpTickets = () => {
  const { helpTicketId } = useParams() // The ID of the currently viewed help ticket
  const [helpTickets, setHelpTickets] = useState([]) // All help tickets
  const [helpTicket, setHelpTicket] = useState(null) // The currently viewed help ticket
  const [loading, setLoading] = useState(true) // True if fetching help ticket data
  const [helpTicketStatusFilter, setHelpTicketStatusFilter] = useState('All') // Allows user to filter help tickets by status
  const [assigneeFilter, setAssigneeFilter] = useState('All') // Allows user to filter help tickets by
  const [helpTicketStatus, setHelpTicketStatus] = useState('') // Allows user to set status for a ticket
  const [commentText, setCommentText] = useState('') // Allows user to add a comment to a ticket
  const [assignee, setAssignee] = useState('') // Allows user to set assignee for a ticket
  const [resolutionText, setResolutionText] = useState('') // Allows user to set resolution for a ticket
  const [ticketSortOldestFirst, setTicketSortOldestFirst] = useState(true) // Allows user to sort tickets
  const [ticketFilterStartDate, setTicketFilterStartDate] = useState(null) // Allows user to filter tickets by date
  const [ticketFilterEndDate, setTicketFilterEndDate] = useState(null) // Allows user to filter tickets by date

  // Fetch all help tickets (within reason) on mount
  useEffect(() => {
    const fetchTickets = async () => {
      console.log('Fetching tickets')
      const response = await fetch('/lgyhub/api/tickets')
      const helpTicketsJson = await response.json()
      setLoading(false)
      setHelpTickets(helpTicketsJson)
    }
    fetchTickets()
  }, [])

  // Set component state from the URL help ticket ID to view plus the fetched help ticket data
  useEffect(() => {
    if (helpTicketId) {
      const helpTicket = helpTickets.find(ht => ht.id === parseInt(helpTicketId))
      if (helpTicket) {
        setHelpTicket(helpTicket)
        // Use empty string instead of null for <select>
        setHelpTicketStatus(helpTicket.status || '')
        setAssignee(helpTicket.assignee || '')
        setResolutionText(helpTicket.resolutionText || '')
      }
    }
  }, [helpTicketId, helpTickets])

  const updateTicketStatus = async ({ helpTicketId, assignee, resolutionText, helpTicketStatus }) => {
    const response = await fetch(`/lgyhub/api/tickets/${helpTicketId}/status`, {
      method: 'POST',
      credentials: 'include',
      headers: new Headers({
        'Content-Type': 'application/json',
        'X-XSRF-TOKEN': getCookieValue('XSRF-TOKEN')
      }),
      body: JSON.stringify({ assignee, resolutionText, status: helpTicketStatus })
    })
    updateHelpTicket(await response.json())
  }

  const addComment = async ({ helpTicketId, commentText }) => {
    setCommentText('')
    const response = await fetch(`/lgyhub/api/tickets/${helpTicketId}/comments`, {
      method: 'POST',
      credentials: 'include',
      headers: new Headers({
        'Content-Type': 'application/json',
        'X-XSRF-TOKEN': getCookieValue('XSRF-TOKEN')
      }),
      body: JSON.stringify({ commentText })
    })
    updateHelpTicket(await response.json())
  }

  const updateHelpTicket = updatedTicket => {
    console.log('updated ticket is ', updatedTicket)
    const indexOfUpdatedTicket = helpTickets.findIndex(ht => ht.id === updatedTicket.id)
    const helpTicketsNew = [...helpTickets]
    helpTicketsNew[indexOfUpdatedTicket] = updatedTicket
    setHelpTickets(helpTicketsNew)
  }

  if (loading) {
    return null
  }

  return (
    <div className='usa-grid'>
      {helpTicketId && helpTicket ? ( // Show a particular help ticket
        <div>
          {/*console.log('helpTicket:', helpTicket)*/}
          <h2>{`Ticket #${helpTicket.id}`}</h2>
          <LgyField label='Date Created' value={new Date(helpTicket.dateCreated).toLocaleString()} />
          <LgyField label='Issue Description' value={helpTicket.ticketDetails.issueDescription} />
          <label htmlFor=''>Status</label>
          <select value={helpTicketStatus} onChange={e => setHelpTicketStatus(e.target.value)}>
            <option value='New'>New</option>
            <option value='Triaged'>Triaged</option>
            <option value='Awaiting Response From User'>Awaiting Response From User</option>
            <option value='Resolved'>Resolved</option>
          </select>
          <label htmlFor=''>Assignee</label>
          <select value={assignee} onChange={e => setAssignee(e.target.value)}>
            <option value='Unassigned'>Unassigned</option>
            <option value='Mike Espinoza'>Mike Espinoza</option>
            <option value='Mike McKinney'>Mike McKinney</option>
            <option value='Anand Roharia'>Anand Roharia</option>
            <option value='RLC'>RLC</option>
            <option value='PMDI'>PMDI</option>
            <option value='Other'>Other</option>
          </select>
          <div>
            <button
              type='button'
              onClick={() =>
                updateTicketStatus({ helpTicketId: helpTicket.id, assignee, helpTicketStatus, resolutionText })
              }
            >
              Update
            </button>
          </div>
          <LgyField
            label='Last Updated'
            value={helpTicket.updated ? new Date(helpTicket.updated).toLocaleString() : 'Never'}
          />
          {helpTicket.userSubmittedId ? (
            <LgyField label='Proofed Username' inlineChildren>
              <LgyLink to={`/lgyhub/admin/users/${helpTicket.userSubmittedId}`}>
                {helpTicket.ticketDetails.proofedUsername}
              </LgyLink>
            </LgyField>
          ) : (
            <LgyField label='Proofed Username' value={helpTicket.ticketDetails.proofedUsername} />
          )}
          <LgyField label='Proofed First Name' value={helpTicket.ticketDetails.proofedFirstName} />
          <LgyField label='Proofed Last Name' value={helpTicket.ticketDetails.proofedLastName} />
          <LgyField label='Name' value={helpTicket.ticketDetails.firstName} />
          <LgyField label='Email' value={helpTicket.ticketDetails.email} />
          <LgyField label='Phone Number' value={helpTicket.ticketDetails.phoneNumber} />
          <LgyField label='User Type' value={helpTicket.ticketDetails.userType} />
          <LgyField label='Issue Type' value={helpTicket.ticketDetails.issueType} />
          <LgyField label='Lender ID' value={helpTicket.ticketDetails.lenderId} />
          <LgyField label='Lender Name' value={helpTicket.ticketDetails.lenderName} />
          <LgyField label='Servicer ID' value={helpTicket.ticketDetails.servicerId} />
          <LgyField label='Servicer Name' value={helpTicket.ticketDetails.servicerName} />
          <LgyField label='Appraiser ID' value={helpTicket.ticketDetails.appraiserId} />
          <LgyField label='Other Requester ID' value={helpTicket.ticketDetails.otherRequesterId} />
          <LgyField label='Inspector ID' value={helpTicket.ticketDetails.inspectorId} />
          <LgyField label='SAR ID' value={helpTicket.ticketDetails.sarId} />
          <LgyField label='Operating System' value={helpTicket.ticketDetails.os} />
          <LgyField label='Browser' value={helpTicket.ticketDetails.browser} />
          <div>
            <h4>Resolution</h4>
            <textarea name='resolutionText' onChange={e => setResolutionText(e.target.value)} value={resolutionText} />
            <button
              type='button'
              onClick={() =>
                updateTicketStatus({ helpTicketId: helpTicket.id, assignee, helpTicketStatus, resolutionText })
              }
            >
              Update
            </button>
          </div>
          <h3>Comments</h3>
          {helpTicket.comments
            .slice()
            .sort((a, b) => a.created - b.created)
            .map(c => (
              <div key={c.id}>{`${c.userFirstName} ${c.userLastName} (${new Date(c.created).toLocaleString()}): ${
                c.text
              }`}</div>
            ))}
          <div>
            <h4>Add Comment</h4>
            <textarea name='commentText' value={commentText} onChange={e => setCommentText(e.target.value)} />
            <button type='button' onClick={() => addComment({ helpTicketId: helpTicket.id, commentText })}>
              Add Comment
            </button>
          </div>
        </div>
      ) : (
        // Show all help tickets
        <div>
          <label htmlFor='helpTicketStatusFilter'>Filter by status</label>
          <select
            id='helpTicketStatusFilter'
            value={helpTicketStatusFilter}
            onChange={e => setHelpTicketStatusFilter(e.target.value)}
          >
            <option value='All'>All</option>
            <option value='New'>New</option>
            <option value='Triaged'>Triaged</option>
            <option value='Awaiting Response From User'>Awaiting Response From User</option>
            <option value='Resolved'>Resolved</option>
          </select>
          <label htmlFor='assigneeFilter'>Filter by assignee</label>
          <select id='assigneeFilter' value={assigneeFilter} onChange={e => setAssigneeFilter(e.target.value)}>
            <option value='All'>All</option>
            <option value='Unassigned'>Unassigned</option>
            <option value='Mike Espinoza'>Mike Espinoza</option>
            <option value='Mike McKinney'>Mike McKinney</option>
            <option value='Anand Roharia'>Anand Roharia</option>
            <option value='RLC'>RLC</option>
            <option value='PMDI'>PMDI</option>
            <option value='Other'>Other</option>
          </select>
          <DatePicker
            id='ticketFilterStartDate'
            name='ticketFilterStartDate'
            selected={ticketFilterStartDate}
            onChange={date => setTicketFilterStartDate(date)}
            dateFormat='MMMM d, yyyy'
            placeholderText='Filter Start Date'
          />
          <DatePicker
            id='ticketFilterEndDate'
            name='ticketFilterEndDate'
            selected={ticketFilterEndDate}
            onChange={date => setTicketFilterEndDate(date)}
            dateFormat='MMMM d, yyyy'
            placeholderText='Filter End Date'
          />
          <table>
            <thead>
              <tr>
                <th
                  onClick={() => {
                    setTicketSortOldestFirst(!ticketSortOldestFirst)
                  }}
                >
                  Date Created ({ticketSortOldestFirst ? 'O>N' : 'N>O'})
                </th>
                <th>Summary</th>
                <th>Status</th>
                <th>Assignee</th>
                <th>Comments</th>
                <th>Updated</th>
              </tr>
            </thead>
            <tbody>
              {helpTickets
                .filter(ht => helpTicketStatusFilter === 'All' || ht.status === helpTicketStatusFilter)
                .filter(ht => assigneeFilter === 'All' || ht.assignee === assigneeFilter)
                .filter(ht => !ticketFilterStartDate || ticketFilterStartDate < ht.dateCreated)
                .filter(ht => !ticketFilterEndDate || ticketFilterEndDate > ht.dateCreated)
                .sort((a, b) => (ticketSortOldestFirst ? a.dateCreated - b.dateCreated : b.dateCreated - a.dateCreated))
                .map(helpTicket => (
                  <tr key={helpTicket.dateCreated}>
                    <td>{new Date(helpTicket.dateCreated).toLocaleString()}</td>
                    <td>
                      <LgyLink to={`/lgyhub/admin/help-tickets/${helpTicket.id}`}>
                        {helpTicket.ticketDetails.issueDescription}
                      </LgyLink>
                    </td>
                    <td>{helpTicket.status}</td>
                    <td>{helpTicket.assignee}</td>
                    <td>{helpTicket.comments.length}</td>
                    <td>{helpTicket.updated ? new Date(helpTicket.updated).toLocaleString() : 'Never'}</td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

export default HelpTickets
